import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  extractUniqueFilterOptions,
  getFilteredContainersAndIndices,
  mapContainersToFilteredRows,
  mapContainersToPaginatedRows,
  normalizeTagValue,
  rowMatchesSelectedTags,
} from './RepeaterUtils';
import { updateWidgetConfig } from 'shared/state/ducks/sites';
import { Config } from './types';

// Minimal Widget type reference (avoid deep import since full export not available here)
interface LocalWidget<T> {
  id: string;
  type?: string;
  config: T;
  children?: string[];
}

const SidebarTitle = styled.div<{
  font?: string;
  fontSize?: string;
  bold?: boolean;
  italic?: boolean;
  color?: string;
  underline?: boolean;
  strikeThrough?: boolean;
}>`
  font-size: ${props => props.fontSize || '14'}px;
  font-weight: ${props => (props.bold ? '600' : '400')};
  font-style: ${props => (props.italic ? 'italic' : 'normal')};
  margin-bottom: 12px;
  color: ${props => props.color || '#1d1929'};
  font-family: ${props => props.font || 'OptumSans'};
  text-decoration: ${p =>
    p.underline ? 'underline' : p.strikeThrough ? 'line-through' : 'none'};
`;

const SidebarOption = styled.label<{
  font?: string;
  fontSize?: string;
  bold?: boolean;
  italic?: boolean;
  color?: string;
  underline?: boolean;
  strikeThrough?: boolean;
}>`
  font-size: ${props => props.fontSize || '14'}px;
  font-weight: ${props => (props.bold ? '600' : '400')};
  font-style: ${props => (props.italic ? 'italic' : 'normal')};
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.color || '#1d1929'};
  font-family: ${props => props.font || 'OptumSans'};
  text-decoration: ${p =>
    p.underline ? 'underline' : p.strikeThrough ? 'line-through' : 'none'};
  margin-bottom: 4px;
`;

export const previewSelectedFilters: Record<string, string[]> = {};

export interface RepeaterSidebarPluginProps {
  widget: LocalWidget<Partial<Config>>;
  currentPage: { pageId: string | number; content?: any }; // minimal shape used in plugin
  siteId: string | number;
  updateCurrentPage: (pageVersion: any) => void;
  onFilterChange?: (value: string, selectedValues: any[]) => void; // optional callback consumed by parent for tracking
}

const isOptionSelected = (
  selectedOptions: string[],
  option: string
): boolean => {
  const normalizedOption = normalizeTagValue(option);
  return selectedOptions.some(
    selected => normalizeTagValue(selected) === normalizedOption
  );
};

const toggleOptionSelection = (
  selectedOptions: string[],
  option: string
): string[] => {
  const normalizedOption = normalizeTagValue(option);
  const optionIndex = selectedOptions.findIndex(
    selected => normalizeTagValue(selected) === normalizedOption
  );

  if (optionIndex >= 0) {
    return selectedOptions.filter((_, index) => index !== optionIndex);
  }

  return [...selectedOptions, option];
};

// Custom event system for filter changes
const filterChangeEvent = new CustomEvent('repeaterFilterChange');

export const triggerFilterChange = () => {
  window.dispatchEvent(filterChangeEvent);
};
export const RepeaterSidebarPlugin: React.FC<RepeaterSidebarPluginProps> = ({
  widget,
  currentPage,
  siteId,
  updateCurrentPage,
  onFilterChange,
}) => {
  const dispatch = useDispatch();
  const { config } = widget;
  const filterBy = config?.filterBy;
  const repeaterData = (config as any)?.repeaterData;
  const allOptions: string[] =
    filterBy && repeaterData?.data
      ? extractUniqueFilterOptions(repeaterData.data, filterBy)
      : [];

  const initialSelectedOptions =
    previewSelectedFilters[widget.id] ?? allOptions;
  const [selectedOptions, setSelectedOptions] = useState(
    initialSelectedOptions
  );

  useEffect(() => {
    if (filterBy && repeaterData?.data) {
      // Only initialize if not already set
      if (!previewSelectedFilters[widget.id]) {
        previewSelectedFilters[widget.id] = selectedOptions as string[];
      }
    }
  }, []);
  if (!filterBy || !repeaterData?.data) return null;
  const handleToggle = (value: any) => {
    const updated = toggleOptionSelection(selectedOptions, value as string);
    const hasSelectedOptions = updated.length > 0;

    // Update the global state immediately
    previewSelectedFilters[widget.id] = updated;

    // Update the local state
    setSelectedOptions(updated);
    onFilterChange?.(value as string, updated);

    // Calculate filtered indexes from selected checkbox values
    const checkboxFilteredIndexes = repeaterData.data
      .map((row, index) =>
        rowMatchesSelectedTags(row[filterBy], updated) ? index : null
      )
      .filter(i => i !== null);

    // When both are enabled, filtered containers are passed to pagination
    if (config.enablePagination) {
      const {
        filteredContainerIds,
        filteredDataIndices,
      } = getFilteredContainersAndIndices(
        repeaterData,
        filterBy,
        updated,
        widget.children || [],
        currentPage
      );

      // Reset pagination to page 1 whenever filters change
      dispatch(
        updateWidgetConfig(
          siteId,
          currentPage.pageId,
          widget.id,
          {
            ...config,
            currentPaginationPage: 1,
          },
          false
        )
      );

      // Apply pagination to only the filtered containers
      mapContainersToPaginatedRows(
        widget.id,
        config.enablePagination,
        currentPage,
        repeaterData,
        config.paginatedItemsPerPage || 9,
        dispatch,
        siteId as number,
        currentPage.pageId as number,
        1, // Reset to page 1
        filteredContainerIds, // filtered containers for pagination
        filteredDataIndices // filtered data indices for proper mapping
      );
    } else {
      mapContainersToFilteredRows({
        widgetId: widget.id,
        currentPage: currentPage as any, // cast to satisfy PageVersion requirement in util
        repeaterData,
        filterBy,
        enableFiltering: true,
        dispatch,
        siteId: siteId as number, // runtime expects number; tests using string are for analytics isolation
        pageId: currentPage.pageId as number,
        checkboxFilteredIndexes,
        applyCheckboxFiltering: true,
        hasSelectedOptions,
        updateCurrentPage,
      });
    }
  };

  return (
    <div
      data-test-id="sidebar"
      style={{
        width: 200,
        minWidth: 200,
        height: '100%',
        backgroundColor: config.sidebarBackgroundColor || '#f3f3f3',
        padding: '16px 12px',
        borderRight: '1px solid #d8d8d8',
        boxSizing: 'border-box',
        fontFamily: 'OptumSans, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SidebarTitle
        data-test-id="sidebar-title"
        font={config.sidebarHeadingFont}
        fontSize={config.sidebarHeadingFontSize}
        bold={config.sidebarHeadingBold}
        italic={config.sidebarHeadingItalic}
        color={config.sidebarHeadingTextColor}
        underline={config.sidebarHeadingUnderline}
        strikeThrough={config.sidebarHeadingStrikeThrough}
      >
        {filterBy}
      </SidebarTitle>
      {allOptions.map(option => (
        <SidebarOption
          key={option as string}
          data-test-id={`sidebar-option-${option}`}
          font={config.sidebarOptionFont}
          fontSize={config.sidebarOptionFontSize}
          bold={config.sidebarOptionBold}
          italic={config.sidebarOptionItalic}
          color={config.sidebarOptionTextColor}
          underline={config.sidebarOptionUnderline}
          strikeThrough={config.sidebarOptionStrikeThrough}
        >
          <input
            data-test-id={`checkbox-${option}`}
            type="checkbox"
            checked={isOptionSelected(selectedOptions, option)}
            onChange={() => handleToggle(option)}
            style={{
              accentColor: config.checkboxButtonColor || '#002677',
            }}
          />
          <span style={{ marginLeft: 6 }}>{option}</span>
        </SidebarOption>
      ))}
    </div>
  );
};
